import type { TaskTimeModeValue } from '@my/tms-api';
import type { CopyFunctions } from './copyFunctions';

/**
 * What "Use this order" will ask the backend to copy. Collected by the panel
 * beside the previous-orders list; the copy itself is a later step, so this is
 * the shape it will send rather than something anything reads yet.
 *
 * <p>
 * Always copied, so not options: the cargo and its service tags, and the
 * tasks with their locations — a copy repeats a job (a single task can still
 * be left out, `excludedTaskIds`). Everything else is the user's choice,
 * starting from the organisation's defaults (Copy Functions card). Nothing
 * ties the new order to the old one.
 * </p>
 */
export interface CopyOrderOptions {
  /** Pallet counts, weights, volumes, loading metres and products on each task. */
  includeQuantities: boolean;
  /** AM / PM / exact times on each task — off leaves every task "Any". */
  includeTimeWindows: boolean;
  /** Each task's notes for the driver. */
  includeTaskNotes: boolean;
  /**
   * Ferry bookings: carrier, route, booking type and accompanied setting,
   * the sailing moved with the dates. What belongs to the one sailing
   * (reference, vessel, voyage, vehicle, trailer, customs) is never copied.
   */
  includeBookings: boolean;
  /**
   * The temperature, on the order and on each task — temperature-controlled
   * cargo only. Unit numbers (Tank IDs / BIC codes) and seals are never
   * copied: next time is usually another unit, and a seal is per run.
   */
  includeTemperature: boolean;
  /** The order's references (customer, load, manifest, …). */
  includeReferences: boolean;
  /** The files attached to the order. */
  includeDocuments: boolean;
  /**
   * The status the new order is created with — the organisation's setting
   * (Copy Functions card), Draft unless support chose Open. Tasks always
   * start Pending.
   */
  status: 'Draft' | 'Open';
  /** Tasks the user unticked — left off the new order. A booking's two halves go together. */
  excludedTaskIds: string[];
  /** The new order's first task date; every other task keeps its distance from it. */
  newStartDate: string;
  /**
   * The first copied task's time: Any / AM / PM / Exact, as on the task form.
   * Null keeps the task's own. Ignored when time windows aren't copied.
   */
  newStartTimeMode: TaskTimeModeValue | null;
  /**
   * With an Exact start, its `HH:mm` (quarter hours). Null keeps the task's
   * own. When the first task had an exact time too, every task with an exact
   * time moves by the same amount; the rest move by whole days.
   */
  newStartTime: string | null;
  /**
   * With time windows NOT copied, each task's date and time are set by hand,
   * keyed by the previous order's task id. A task with no entry takes its date
   * moved by the start date's shift, and Any.
   */
  taskTimes: Record<string, TaskTimeEdit>;
  /** Price the new order from the rate card, or copy the previous order's lines. */
  charges: 'Reprice' | 'CopyPrevious';
}

/** A task's date and time on the new order, set by hand. */
export interface TaskTimeEdit {
  /** `yyyy-mm-dd`, or null for no date. */
  date: string | null;
  mode: TaskTimeModeValue;
  /** `HH:mm` when `mode` is Exact. */
  time: string | null;
}

/** The options a freshly picked order starts with — the organisation's ticked-by-default boxes. */
export function defaultCopyOrderOptions(today: string, defaults: CopyFunctions): CopyOrderOptions {
  return {
    includeQuantities: defaults.ordersCopyQuantities,
    includeTimeWindows: defaults.ordersCopyTimeWindows,
    includeTaskNotes: defaults.ordersCopyTaskNotes,
    includeBookings: defaults.ordersCopyBookings,
    includeTemperature: defaults.ordersCopyTemperature,
    includeReferences: defaults.ordersCopyReferences,
    includeDocuments: defaults.ordersCopyDocuments,
    status: defaults.ordersCopyStartsOpen ? 'Open' : 'Draft',
    excludedTaskIds: [],
    newStartDate: today,
    newStartTimeMode: null,
    newStartTime: null,
    taskTimes: {},
    charges: 'Reprice',
  };
}

/** Today as `yyyy-mm-dd` in the user's own timezone. */
export function todayIso(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

/** Whole days from `from` to `to`, both `yyyy-mm-dd`. Computed in UTC so DST can't shave an hour. */
export function daysBetween(from: string, to: string): number {
  return Math.round((utc(to) - utc(from)) / 86_400_000);
}

/** `iso` moved by `days`, as `yyyy-mm-dd`. */
export function shiftIsoDate(iso: string, days: number): string {
  return new Date(utc(iso) + days * 86_400_000).toISOString().slice(0, 10);
}

/** `HH:mm` → minutes after midnight; null when it isn't a time. */
export function minutesOfDay(time: string | null | undefined): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(time ?? '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** A date and `HH:mm` time moved by `minutes`, rolling over midnight either way. */
export function shiftDateTime(
  date: string,
  time: string,
  minutes: number,
): { date: string; time: string } {
  const total = (minutesOfDay(time) ?? 0) + minutes;
  const days = Math.floor(total / 1440);
  const rest = total - days * 1440;
  const hh = String(Math.floor(rest / 60)).padStart(2, '0');
  const mm = String(rest % 60).padStart(2, '0');
  return { date: shiftIsoDate(date, days), time: `${hh}:${mm}` };
}

function utc(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1);
}
